/**
 * /api/app 求职者端接口封装（三入口前缀隔离，见技术开发文档 §4.4）。
 *
 * 类型对齐 contracts/openapi.yaml（生成于 @/types/generated/api），
 * 统一经 @/api/client 请求；业务失败抛 BizError（页面 catch 后展示 message）。
 */
import { requestJson } from '@/api/client'
import type { components } from '@/types/generated/api'

export { agentApi } from './agent'
export type { ChatCallbacks, ChatParams } from './agent'
export { invoiceApi } from './invoice'
export type {
  InvoiceTitle,
  InvoiceTitleInput,
  InvoiceContentType,
  InvoiceRequest,
  ApplyInvoiceInput,
} from './invoice'

type LoginRequest = components['schemas']['LoginRequest']
type LoginResult = components['schemas']['LoginResult']
type RegisterRequest = components['schemas']['RegisterRequest']
type AppUserInfo = components['schemas']['AppUserInfo']
type CandidateProfile = components['schemas']['CandidateProfile']
type JobPage = components['schemas']['JobPage']
type JobDetail = components['schemas']['JobDetail']
type RecommendationCard = components['schemas']['RecommendationCard']
type ApplicationSummary = components['schemas']['ApplicationSummary']
type ApplicationEvent = components['schemas']['ApplicationEvent']
type CartItem = components['schemas']['CartItem']
type Consent = components['schemas']['Consent']
type CreateApplicationRequest = components['schemas']['CreateApplicationRequest']
type CloseApplicationRequest = components['schemas']['CloseApplicationRequest']
type AddToCartRequest = components['schemas']['AddToCartRequest']
type CreateConsentRequest = components['schemas']['CreateConsentRequest']
type CreateBookingRequest = components['schemas']['CreateBookingRequest']
type CreateReviewRequest = components['schemas']['CreateReviewRequest']
type CreateOrderRequest = components['schemas']['CreateOrderRequest']
type RefundRequest = components['schemas']['RefundRequest']
type PayRequest = components['schemas']['PayRequest']
type PaymentResult = components['schemas']['PaymentResult']

/** 职位收藏（FR-JOB-03） */
export interface JobFavorite {
  id: number
  candidateUserId: number
  jobId: number
  createdAt: string
}

/** 求职目标（FR-PRF-02；待 openapi-typescript 重新生成后切换到 generated 类型） */
export interface CareerTarget {
  targetId: number
  candidateId: number
  title: string
  cities: string | null
  industries: string | null
  companies: string | null
  jobTitles: string | null
  timeWindowStart: string | null
  timeWindowEnd: string | null
  salaryMin: number | null
  salaryMax: number | null
  priority: number
  enabled: number
  remark: string | null
  createdAt: string
  updatedAt: string
}

/** 求职目标新增/更新输入（更新时所有字段可选） */
export interface CareerTargetInput {
  title?: string
  cities?: string
  industries?: string
  companies?: string
  jobTitles?: string
  timeWindowStart?: string
  timeWindowEnd?: string
  salaryMin?: number
  salaryMax?: number
  priority?: number
  enabled?: number
  remark?: string
}

/** 提交前确认单预览（FR-APL-03） */
export interface SubmitPreview {
  totalApplications: number
  items: Array<{
    applicationId: number
    applyMode: string
    currentStage: string
    jobTitle?: string
    employer?: string
    city?: string
    applyChannel?: string
    applyUrl?: string
  }>
}

/** 求职策略（FR-AGT-03） */
export interface JobSearchStrategy {
  generatedAt: number
  source: 'RULE_BASED' | 'AI_GENERATED' | 'COLD_START'
  profileSummary: Record<string, unknown>
  targetSummary: Record<string, unknown>
  actions: StrategyAction[]
  timelineTips: string[]
}

/** 策略行动项 */
export interface StrategyAction {
  code: string
  title: string
  description: string
  navigateTo: string
  urgent: boolean
}

/** 阶段任务推荐（FR-AGT-04） */
export interface StageTasks {
  tasks: StageTask[]
}

/** 阶段任务项（FR-AGT-06 增强：含 taskId、source、priority） */
export interface StageTask {
  taskId?: number
  code: string
  title: string
  description: string
  navigateTo: string
  source?: string
  priority?: number
  dueAt?: string | null
}

/** 导师档案（门户展示，后端 mentor_profile 表字段） */
export interface MentorProfile {
  mentorId: number
  displayName: string
  title?: string
  company?: string
  industry?: string
  /** JSON 数组字符串（契约口径，渲染时用 parseJsonArray 解析） */
  expertiseAreas?: string
  bio?: string
  avatarFileId?: number
  yearsOfExp?: number
  verificationStatus?: string
  hourlyRate?: number
  availableSlots?: string[]
}

/** AI 导师推荐结果 */
export interface AppMentorRecommendation {
  mentorId: number | string
  displayName: string
  title?: string
  company?: string
  expertiseAreas: string[]
  avgRating?: number
  reviewCount?: number
  matchScore: number
  matchReason?: string
}

/** 快捷选项 */
export interface AppQuickAction {
  id: number
  label: string
  value: string
  promptHint?: string
  sortOrder: number
  enabled: number
}

/** 导师详情（含评分与评价数） */
export interface MentorDetail {
  profile: MentorProfile
  avgRating: number | null
  reviewCount: number
}

/** 导师排期项（纯时段可用性；类型/定价见 MentorServiceItem） */
export interface MentorScheduleItem {
  scheduleId: number
  mentorId: number
  dayOfWeek: number
  startTime: string
  endTime: string
  slotMinutes: number
  enabled: number
}

/** 导师服务（独立售卖实体，FR-MTR-09） */
export interface MentorServiceItem {
  serviceId: number
  mentorId: number
  serviceType: string
  serviceTypeLabel: string | null
  price: number
  enabled: number
  createdAt: string
  updatedAt: string
}

/** 在售服务类型筛选选项（value 为字典 code，label 服务端拼） */
export interface MentorServiceTypeOption {
  serviceType: string
  serviceTypeLabel: string | null
}

/** C 端预约信息 */
export interface AppBookingInfo {
  bookingId: number
  mentorId: number
  candidateUserId: number
  orderId: number | null
  scheduleId: number
  bookingDate: string
  startTime: string
  endTime: string
  serviceType: string
  /** 服务类型展示名（服务端取字典注入，B6） */
  serviceTypeLabel: string | null
  status: string
  candidateRequest: string | null
  resumeAuthorized: number
  videoUrl: string | null
  mentorFeedback: string | null
  cancelReason: string | null
  createdAt: string
}

/** C 端评价信息 */
export interface AppReviewInfo {
  reviewId: number
  bookingId: number
  mentorId: number
  rating: number
  content: string
  createdAt: string
}

/** 导师申请请求 */
export interface MentorApplyRequest {
  displayName: string
  title?: string
  company?: string
  industry?: string
  expertiseAreas?: string
  bio?: string
  yearsOfExp?: number
}

/** 订单信息 */
export interface OrderInfo {
  orderId: number
  orderType: string
  status: string
  userId: number
  title: string
  totalAmount: number
  paidAmount: number | null
  currency: string
  refType: string | null
  refId: number | null
  expireAt: string | null
  paidAt: string | null
  completedAt: string | null
  cancelledAt: string | null
  remark: string | null
  createdAt: string
  updatedAt: string
}

/** 退款记录 */
export interface RefundInfo {
  refundId: number
  orderId: number
  refundAmount: number
  reason: string
  refundStatus: string
  createdAt: string
}

export const appApi = {
  auth: {
    /** 求职者注册（成功即签发 JWT，注册后免二次登录） */
    register(payload: RegisterRequest): Promise<LoginResult> {
      return requestJson<LoginResult>({
        method: 'POST',
        url: '/api/app/auth/register',
        data: payload,
      })
    },
    /** 求职者登录（后端校验 ROLE_CANDIDATE，三入口隔离） */
    login(payload: LoginRequest): Promise<LoginResult> {
      return requestJson<LoginResult>({
        method: 'POST',
        url: '/api/app/auth/login',
        data: payload,
      })
    },
    /** 当前登录用户信息（会话恢复用） */
    me(): Promise<AppUserInfo> {
      return requestJson<AppUserInfo>({ method: 'GET', url: '/api/app/auth/me' })
    },
  },
  profile: {
    /** 获取候选人画像（Master CV，唯一事实来源） */
    getProfile(): Promise<CandidateProfile> {
      return requestJson<CandidateProfile>({ method: 'GET', url: '/api/app/career/profile' })
    },
    /** 更新画像（全量覆盖语义，保存即置 CONFIRMED） */
    updateProfile(payload: CandidateProfile): Promise<CandidateProfile> {
      return requestJson<CandidateProfile>({
        method: 'PUT',
        url: '/api/app/career/profile',
        data: payload,
      })
    },
  },
  /** 求职目标 CRUD（FR-PRF-02） */
  targets: {
    /** 获取全部求职目标（含停用） */
    list(): Promise<CareerTarget[]> {
      return requestJson<CareerTarget[]>({ method: 'GET', url: '/api/app/career/targets' })
    },
    /** 新增求职目标 */
    add(payload: CareerTargetInput): Promise<CareerTarget> {
      return requestJson<CareerTarget>({
        method: 'POST',
        url: '/api/app/career/targets',
        data: payload,
      })
    },
    /** 更新求职目标 */
    update(targetId: number, payload: CareerTargetInput): Promise<CareerTarget> {
      return requestJson<CareerTarget>({
        method: 'PUT',
        url: `/api/app/career/targets/${targetId}`,
        data: payload,
      })
    },
    /** 删除求职目标（软删） */
    remove(targetId: number): Promise<void> {
      return requestJson<void>({ method: 'DELETE', url: `/api/app/career/targets/${targetId}` })
    },
  },
  /** 求职策略与任务编排（FR-AGT-03/04/06） */
  strategy: {
    /** 获取求职策略（冷启动/刷新） */
    get(): Promise<JobSearchStrategy> {
      return requestJson<JobSearchStrategy>({ method: 'GET', url: '/api/app/career/strategy' })
    },
    /** 获取阶段任务推荐 */
    getTasks(): Promise<StageTasks> {
      return requestJson<StageTasks>({ method: 'GET', url: '/api/app/career/tasks' })
    },
    /** 更新任务状态（FR-AGT-06） */
    updateTaskStatus(taskId: number, status: string, skipReason?: string): Promise<void> {
      return requestJson<void>({
        method: 'PUT',
        url: `/api/app/career/tasks/${taskId}/status`,
        data: { status, skipReason },
      })
    },
  },
  jobs: {
    /** 职位搜索与筛选（FR-JOB-04） */
    list(params?: {
      city?: string
      cohortType?: string
      keyword?: string
      pageNum?: number
      pageSize?: number
    }): Promise<JobPage> {
      return requestJson<JobPage>({ method: 'GET', url: '/api/app/jobs', params })
    },
    /** 职位详情 */
    getDetail(id: string | number): Promise<JobDetail> {
      return requestJson<JobDetail>({ method: 'GET', url: `/api/app/jobs/${id}` })
    },
    /** 每日推荐职位（规则匹配引擎：技能/城市/岗位/时间窗口四维打分） */
    getRecommendations(limit?: number): Promise<RecommendationCard[]> {
      return requestJson<RecommendationCard[]>({
        method: 'GET',
        url: '/api/app/jobs/recommendations',
        params: { limit },
      })
    },
    /** 提交推荐反馈（FR-MCH-05） */
    submitFeedback(jobId: string | number, feedbackType: string, reason?: string): Promise<void> {
      return requestJson<void>({
        method: 'POST',
        url: '/api/app/jobs/feedback',
        data: { jobId, feedbackType, reason },
      })
    },
    /** 职位版本历史（FR-JOB-05） */
    getVersions(id: string | number): Promise<unknown[]> {
      return requestJson<unknown[]>({
        method: 'GET',
        url: `/api/app/jobs/${id}/versions`,
      })
    },
    /** 收藏职位（FR-JOB-03） */
    addFavorite(id: string | number): Promise<void> {
      return requestJson<void>({
        method: 'POST',
        url: `/api/app/jobs/${id}/favorite`,
      })
    },
    /** 取消收藏 */
    removeFavorite(id: string | number): Promise<void> {
      return requestJson<void>({
        method: 'DELETE',
        url: `/api/app/jobs/${id}/favorite`,
      })
    },
    /** 查询是否已收藏 */
    isFavorited(id: string | number): Promise<{ favorited: boolean }> {
      return requestJson<{ favorited: boolean }>({
        method: 'GET',
        url: `/api/app/jobs/${id}/favorited`,
      })
    },
    /** 我的收藏列表 */
    listFavorites(): Promise<JobFavorite[]> {
      return requestJson<JobFavorite[]>({
        method: 'GET',
        url: '/api/app/jobs/favorites',
      })
    },
    /** 相似岗位推荐（FR-MCH-05） */
    getSimilar(id: string | number, limit?: number): Promise<JobDetail[]> {
      return requestJson<JobDetail[]>({
        method: 'GET',
        url: `/api/app/jobs/${id}/similar`,
        params: { limit },
      })
    },
  },
  applications: {
    /** 申请列表（Pipeline 看板数据源，可选 stage 过滤） */
    list(stage?: string): Promise<ApplicationSummary[]> {
      return requestJson<ApplicationSummary[]>({
        method: 'GET',
        url: '/api/app/applications',
        params: stage ? { stage } : undefined,
      })
    },
    /** 申请详情 */
    get(id: string | number): Promise<ApplicationSummary> {
      return requestJson<ApplicationSummary>({ method: 'GET', url: `/api/app/applications/${id}` })
    },
    /** 创建申请（起点 Saved） */
    create(payload: CreateApplicationRequest): Promise<ApplicationSummary> {
      return requestJson<ApplicationSummary>({
        method: 'POST',
        url: '/api/app/applications',
        data: payload,
      })
    },
    /** 事件流时间线（append-only） */
    listEvents(id: string | number): Promise<ApplicationEvent[]> {
      return requestJson<ApplicationEvent[]>({
        method: 'GET',
        url: `/api/app/applications/${id}/events`,
      })
    },
    /** 提交申请（Authorized → Submitted，需有效授权） */
    submit(id: string | number): Promise<ApplicationSummary> {
      return requestJson<ApplicationSummary>({
        method: 'POST',
        url: `/api/app/applications/${id}/submit`,
      })
    },
    /** 关闭申请（终态，留痕） */
    close(id: string | number, payload?: CloseApplicationRequest): Promise<ApplicationSummary> {
      return requestJson<ApplicationSummary>({
        method: 'POST',
        url: `/api/app/applications/${id}/close`,
        data: payload,
      })
    },
    /** 手动推进阶段（FR-PIP-01 拖拽推进） */
    advanceStage(
      id: string | number,
      payload: { targetStage: string; note?: string },
    ): Promise<ApplicationSummary> {
      return requestJson<ApplicationSummary>({
        method: 'POST',
        url: `/api/app/applications/${id}/advance-stage`,
        data: payload,
      })
    },
    /** 提交前确认单预览（FR-APL-03） */
    previewSubmit(applicationIds: (string | number)[]): Promise<SubmitPreview> {
      return requestJson<SubmitPreview>({
        method: 'GET',
        url: '/api/app/applications/preview-submit',
        params: { applicationIds: applicationIds.join(',') },
      })
    },
  },
  consents: {
    /** 按申请查授权记录 */
    listByApplication(applicationId: string | number): Promise<Consent[]> {
      return requestJson<Consent[]>({
        method: 'GET',
        url: '/api/app/apply/consents',
        params: { application_id: applicationId },
      })
    },
    /** 创建授权（逐岗位逐批次，含数据类别与材料版本披露） */
    create(payload: CreateConsentRequest): Promise<Consent> {
      return requestJson<Consent>({
        method: 'POST',
        url: '/api/app/apply/consents',
        data: payload,
      })
    },
    /** 撤回授权（仅改状态留痕，不删记录） */
    withdraw(id: string, reason?: string): Promise<Consent> {
      return requestJson<Consent>({
        method: 'POST',
        url: `/api/app/apply/consents/${id}/withdraw`,
        data: reason ? { reason } : undefined,
      })
    },
  },
  cart: {
    /** 申请篮列表（可选状态过滤） */
    list(status?: string): Promise<CartItem[]> {
      return requestJson<CartItem[]>({
        method: 'GET',
        url: '/api/app/apply/cart',
        params: status ? { status } : undefined,
      })
    },
    /** 加入申请篮（每岗位独立材料与授权） */
    add(payload: AddToCartRequest): Promise<CartItem> {
      return requestJson<CartItem>({
        method: 'POST',
        url: '/api/app/apply/cart',
        data: payload,
      })
    },
    /** 移出申请篮（软删除） */
    remove(id: string | number): Promise<void> {
      return requestJson<void>({ method: 'DELETE', url: `/api/app/apply/cart/${id}` })
    },
  },
  mentors: {
    /** 导师列表（匿名开放，门户引流；serviceType 按在售服务筛选） */
    list(params?: { industry?: string; serviceType?: string }): Promise<MentorProfile[]> {
      return requestJson<MentorProfile[]>({
        method: 'GET',
        url: '/api/app/mentors',
        params,
      })
    },
    /** 导师详情（匿名开放） */
    getDetail(id: string | number): Promise<MentorDetail> {
      return requestJson<MentorDetail>({
        method: 'GET',
        url: `/api/app/mentors/${id}`,
      })
    },
    /** 导师可用排期（匿名开放） */
    getSchedules(id: string | number): Promise<MentorScheduleItem[]> {
      return requestJson<MentorScheduleItem[]>({
        method: 'GET',
        url: `/api/app/mentors/${id}/schedules`,
      })
    },
    /** 导师服务卡片（匿名开放，仅上架，FR-MTR-13） */
    getServices(id: string | number): Promise<MentorServiceItem[]> {
      return requestJson<MentorServiceItem[]>({
        method: 'GET',
        url: `/api/app/mentors/${id}/services`,
      })
    },
    /** 在售服务类型选项（服务端拼字典 label，B6） */
    getServiceTypes(): Promise<MentorServiceTypeOption[]> {
      return requestJson<MentorServiceTypeOption[]>({
        method: 'GET',
        url: '/api/app/mentors/service-types',
      })
    },
    /** 查询我的导师申请状态（未申请返回 null） */
    applyStatus(): Promise<MentorProfile | null> {
      return requestJson<MentorProfile | null>({
        method: 'GET',
        url: '/api/app/mentors/apply/status',
      })
    },
    /** 申请成为导师 */
    apply(payload: MentorApplyRequest): Promise<MentorProfile> {
      return requestJson<MentorProfile>({
        method: 'POST',
        url: '/api/app/mentors/apply',
        data: payload,
      })
    },
    /** 获取导师推荐（AI 匹配引擎） */
    getRecommendations(payload: {
      targetRole: string
      targetIndustry?: string
      currentLevel?: string
      painPoints?: string[]
      limit?: number
    }): Promise<{ mentors: AppMentorRecommendation[]; fallbackMsg?: string }> {
      return requestJson({
        method: 'POST',
        url: '/api/app/mentor-recommend/recommendations',
        data: payload,
      })
    },
    /** 获取快捷选项 */
    getQuickActions(): Promise<AppQuickAction[]> {
      return requestJson<AppQuickAction[]>({
        method: 'GET',
        url: '/api/app/mentor-recommend/quick-actions',
      })
    },
    /** 公开页功能开关（AI 导师推荐） */
    getRecommendConfig(): Promise<{ enabled: boolean; publicEnabled: boolean }> {
      return requestJson<{ enabled: boolean; publicEnabled: boolean }>({
        method: 'GET',
        url: '/api/app/mentor-recommend/config',
      })
    },
  },
  bookings: {
    /** 发起导师预约 */
    create(mentorId: string | number, payload: CreateBookingRequest): Promise<AppBookingInfo> {
      return requestJson<AppBookingInfo>({
        method: 'POST',
        url: `/api/app/mentors/${mentorId}/bookings`,
        data: payload,
      })
    },
    /** 我的预约列表 */
    list(status?: string): Promise<AppBookingInfo[]> {
      return requestJson<AppBookingInfo[]>({
        method: 'GET',
        url: '/api/app/mentors/bookings',
        params: status ? { status } : undefined,
      })
    },
    /** 取消预约 */
    cancel(id: string | number, reason?: string): Promise<AppBookingInfo> {
      return requestJson<AppBookingInfo>({
        method: 'POST',
        url: `/api/app/mentors/bookings/${id}/cancel`,
        data: reason ? { reason } : undefined,
      })
    },
    /** 提交评价 */
    createReview(bookingId: string | number, payload: CreateReviewRequest): Promise<AppReviewInfo> {
      return requestJson<AppReviewInfo>({
        method: 'POST',
        url: `/api/app/mentors/bookings/${bookingId}/review`,
        data: payload,
      })
    },
    /** 查看评价 */
    getReview(bookingId: number): Promise<AppReviewInfo> {
      return requestJson<AppReviewInfo>({
        method: 'GET',
        url: `/api/app/mentors/bookings/${bookingId}/review`,
      })
    },
  },
  orders: {
    /** 创建订单 */
    create(payload: CreateOrderRequest): Promise<OrderInfo> {
      return requestJson<OrderInfo>({
        method: 'POST',
        url: '/api/app/orders',
        data: payload,
      })
    },
    /** 我的订单列表 */
    list(): Promise<OrderInfo[]> {
      return requestJson<OrderInfo[]>({ method: 'GET', url: '/api/app/orders' })
    },
    /** 订单详情 */
    get(id: number): Promise<OrderInfo> {
      return requestJson<OrderInfo>({ method: 'GET', url: `/api/app/orders/${id}` })
    },
    /** 取消订单 */
    cancel(id: number): Promise<OrderInfo> {
      return requestJson<OrderInfo>({ method: 'POST', url: `/api/app/orders/${id}/cancel` })
    },
    /** 发起支付（支持多渠道：SIMULATED/WECHAT_NATIVE/ALIPAY） */
    pay(id: number, payload?: PayRequest): Promise<PaymentResult> {
      return requestJson<PaymentResult>({
        method: 'POST',
        url: `/api/app/orders/${id}/pay`,
        data: payload,
      })
    },
    /** 完成订单 */
    complete(id: number): Promise<OrderInfo> {
      return requestJson<OrderInfo>({ method: 'POST', url: `/api/app/orders/${id}/complete` })
    },
    /** 申请退款 */
    refund(id: number, payload: RefundRequest): Promise<RefundInfo> {
      return requestJson<RefundInfo>({
        method: 'POST',
        url: `/api/app/orders/${id}/refund`,
        data: payload,
      })
    },
  },
}
